import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../../components/ProductCard';
import FamiliaCard from '../../components/FamiliaCard';
import CatalogueHeader from './CatalogueHeader';
import CatalogueSidebar from './CatalogueSidebar';
import CatalogueGrid from './CatalogueGrid';
import './Catalogue.css';
import { DEFAULT_THUMB_URL } from '../../utils/storage';
import {
  getFamiliasWithThumbnails,
  getFamiliasByIds,
  getAllFamilias,
} from '../../data/familias';
import {
  getVisibleSubfamiliasWithPrice,
  getVisibleSubfamiliaThumbs,
  getVisibleSubfamiliaFamiliaIds,
  getAllSubfamilias,
} from '../../data/subfamilias';

// Add language prop to the component
function Catalogue({ lang = 'es' }) {
  // Fix: Use actual values instead of relying on environment variables that might be undefined
  const location = useLocation();
  const navigate = useNavigate();
  
  // Translation dictionary (move to a separate file for larger projects)
  const translations = {
    es: {
      title: "Catálogo de Productos",
      filter: "Filtrar",
      allTypes: "TODOS LOS TIPOS",
      clear: "Limpiar filtros",
      sort: "Ordenar",
      sortNameAsc: "Nombre (A-Z)",
      sortNameDesc: "Nombre (Z-A)",
      sortPriceAsc: "Precio (menor a mayor)",
      sortPriceDesc: "Precio (mayor a menor)",
      noProducts: "No hay subfamilias que coincidan con los filtros.",
      add: "Agregar",
      id: "ID:"
    },
    en: {
      title: "Product Catalogue",
      filter: "Filter",
      allTypes: "ALL TYPES",
      clear: "Clear filters",
      sort: "Sort",
      sortNameAsc: "Name (A-Z)",
      sortNameDesc: "Name (Z-A)",
      sortPriceAsc: "Price (low to high)",
      sortPriceDesc: "Price (high to low)",
      noProducts: "No subfamilies match the filters.",
      add: "Add",
      id: "ID:"
    }
  };
  const t = translations[lang] || translations.es;

  const [products, setProducts] = useState([]); // subfamilias when filtering
  const [familiasList, setFamiliasList] = useState([]); // for filter dropdown
  const [familiasWithThumbnails, setFamiliasWithThumbnails] = useState([]); // familia cards with thumbnails
  const [filters, setFilters] = useState({
    familia_id: ''
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOption, setSortOption] = useState('nombre-asc');
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [thumbnailRotation, setThumbnailRotation] = useState({});
  // Remove showingFamilias from state, derive from URL
  const [familias, setFamilias] = useState([]);
  const [subfamilias, setSubfamilias] = useState([]);
  const [selectedFamiliaId, setSelectedFamiliaId] = useState(null);

  // Handle URL parameters for filtering
  // Always derive showingFamilias from URL param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const familiaId = params.get('familia');
    if (familiaId && !isNaN(familiaId)) {
      const familiaIdInt = parseInt(familiaId);
      setFilters(prev => ({ ...prev, familia_id: familiaIdInt }));
      setSelectedFamiliaId(familiaIdInt);
    } else {
      setFilters(prev => ({ ...prev, familia_id: '' }));
      setSelectedFamiliaId(null);
    }
    setInitialLoad(false);
  }, [location.search]);

  const showingFamilias = !filters.familia_id;

  // Handle filter change and update URL
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    
    // Update URL - use 'familia' in URL but 'familia_id' in state
    const params = new URLSearchParams(location.search);
    const urlKey = filterKey === 'familia_id' ? 'familia' : filterKey;
    
    if (value) {
      params.set(urlKey, value);
    } else {
      params.delete(urlKey);
    }
    
    const newUrl = params.toString() ? `/catalogue?${params.toString()}` : '/catalogue';
    navigate(newUrl, { replace: true });
  };

  useEffect(() => {
    // Don't fetch until we've processed the URL parameters
    if (initialLoad) return;
    
    if (showingFamilias) {
      fetchFamiliasWithThumbnails();
    } else {
      fetchSubfamilias();
    }
    // eslint-disable-next-line
  }, [filters.familia_id, initialLoad, showingFamilias]);

  async function fetchSubfamilias() {
    setLoading(true);
    try {
      const productsWithPrice = await getVisibleSubfamiliasWithPrice({
        familiaId: filters.familia_id,
      });
      setProducts(productsWithPrice);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  }

  async function fetchFamiliasWithThumbnails() {
    setLoading(true);
    
    try {
      // First, get familias with their thumbnails
      const familiasData = await getFamiliasWithThumbnails();

      // Get subfamilias count and thumbnails for each familia
      const subfamiliasData = await getVisibleSubfamiliaThumbs();

      // Process familias with thumbnail logic
      const processFamilias = () => {
        return familiasData
          .map(familia => {
            // Get subfamilias for this familia
            const subfamilias = subfamiliasData.filter(sub => sub.familia_id === familia.id);
            const subfamiliaCount = subfamilias.length;

            // Skip familias with no visible subfamilias
            if (subfamiliaCount === 0) return null;

            let currentThumbnail;
            let validThumbnails = [];

            // Prefer familia_thumbnail first
            if (familia.familia_thumbnail && familia.familia_thumbnail.length > 0) {
              validThumbnails = familia.familia_thumbnail.map(t => t.url).filter(Boolean);
              currentThumbnail = validThumbnails[0];
            }
            // If no familia_thumbnail, fallback to subfamilia thumbnails
            if (validThumbnails.length === 0) {
              validThumbnails = subfamilias
                .map(sub => sub.thumbnail)
                .filter(thumb => thumb && thumb.trim() !== '');
              currentThumbnail = validThumbnails[0] || null;
            }

            return {
              ...familia,
              subfamiliaCount,
              currentThumbnail,
              validThumbnails
            };
          })
          .filter(Boolean); // Remove null entries
      };

      setFamiliasWithThumbnails(processFamilias());
      setLoading(false);

      // Set up thumbnail rotation for familias without their own thumbnails
      const rotationTimer = setInterval(() => {
        setFamiliasWithThumbnails(prevFamilias => {
          // Update rotation state
          setThumbnailRotation(prev => {
            const newRotation = { ...prev };
            prevFamilias.forEach(familia => {
              // Only rotate if familia doesn't have its own thumbnail
              if (!familia.familia_thumbnail || familia.familia_thumbnail.length === 0) {
                if (familia.validThumbnails && familia.validThumbnails.length > 1) {
                  newRotation[familia.id] = (newRotation[familia.id] || 0) + 1;
                }
              }
            });
            return newRotation;
          });

          // Re-process familias with new rotation
          return processFamilias();
        });
      }, 5000); // Change thumbnail every 5 seconds (slower)

      // Cleanup timer on component unmount
      return () => clearInterval(rotationTimer);

    } catch (error) {
      console.error('Error fetching familias:', error);
      setLoading(false);
    }
  }

  useEffect(() => {
    async function fetchFamilias() {
      try {
        // First, get all non-hidden subfamilias to find which familias have visible products
        const subfamilias = await getVisibleSubfamiliaFamiliaIds();

        // Get unique familia IDs that have at least one non-hidden subfamilia
        const familiaIdsWithVisibleProducts = [...new Set(subfamilias.map(s => s.familia_id))];

        // Fetch only those familias
        const data = await getFamiliasByIds(familiaIdsWithVisibleProducts);
        setFamiliasList(data || []);
      } catch (error) {
        console.error('Error fetching familias:', error);
      }
    }
    fetchFamilias();
    // eslint-disable-next-line
  }, []);

  // Sorting logic with useMemo for performance
  const sortedProducts = useMemo(() => {
    if (showingFamilias) {
      return [...familiasWithThumbnails].sort((a, b) => {
        if (sortOption === 'nombre-asc') {
          return a.nombre.localeCompare(b.nombre);
        }
        if (sortOption === 'nombre-desc') {
          return b.nombre.localeCompare(a.nombre);
        }
        // For familias, we can sort by product count
        if (sortOption === 'precio-asc') { // Treat as "count ascending"
          return a.subfamiliaCount - b.subfamiliaCount;
        }
        if (sortOption === 'precio-desc') { // Treat as "count descending"
          return b.subfamiliaCount - a.subfamiliaCount;
        }
        return 0;
      });
    } else {
      return [...products].sort((a, b) => {
        if (sortOption === 'nombre-asc') {
          return a.nombre.localeCompare(b.nombre);
        }
        if (sortOption === 'nombre-desc') {
          return b.nombre.localeCompare(a.nombre);
        }
        if (sortOption === 'precio-asc') {
          if (a.price === null) return 1;
          if (b.price === null) return -1;
          return a.price - b.price;
        }
        if (sortOption === 'precio-desc') {
          if (a.price === null) return 1;
          if (b.price === null) return -1;
          return b.price - a.price;
        }
        return 0;
      });
    }
  }, [products, familiasWithThumbnails, sortOption, showingFamilias]);

  useEffect(() => {
    async function fetchData() {
      try {
        setFamilias(await getAllFamilias());
        setSubfamilias(await getAllSubfamilias());
      } catch (err) {
        console.error('Error fetching catalogue data:', err);
      }
      // No need to set showingFamilias, it's derived from URL
    }
    fetchData();
  }, []);

  return (
    <div className="catalogue-page">
      <CatalogueHeader title={t.title} />
      <div className="catalogue-main">
        {/* Show filters only when viewing subfamilias */}
        {!showingFamilias && (
          <CatalogueSidebar filtersOpen={filtersOpen}>
            {/* Filters */}
            <div className={`catalogue-filters${filtersOpen ? ' open' : ''}`}>
              <div className="catalogue-filters-header">
                <span className="catalogue-filters-title">
                  <span className="desktop-only">{t.filter}</span>
                  <span className="mobile-only">{t.filter}</span>
                </span>
                
              </div>
              <div className={`catalogue-filters-content${filtersOpen ? ' show' : ''} mobile-always-show`}>
                {/* Desktop: Radio buttons */}
                <div className="filter-radio-group desktop-only">
                  <label className={`filter-radio-option ${filters.familia_id === '' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="familia-filter"
                      value=""
                      checked={filters.familia_id === ''}
                      onChange={e => handleFilterChange('familia_id', '')}
                    />
                    <span className="filter-radio-label">{t.allTypes}</span>
                  </label>
                  {familiasList.map(familia => (
                    <label key={familia.id} className={`filter-radio-option ${filters.familia_id === familia.id ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="familia-filter"
                        value={familia.id}
                        checked={filters.familia_id === familia.id}
                        onChange={e => handleFilterChange('familia_id', parseInt(e.target.value))}
                      />
                      <span className="filter-radio-label">{familia.nombre}</span>
                    </label>
                  ))}
                </div>

                {/* Mobile: Dropdown */}
                <select
                  className="mobile-only"
                  value={filters.familia_id}
                  onChange={e => handleFilterChange('familia_id', e.target.value ? parseInt(e.target.value) : '')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    marginBottom: '8px'
                  }}
                >
                  <option value="">{t.allTypes}</option>
                  {familiasList.map(familia => (
                    <option key={familia.id} value={familia.id}>
                      {familia.nombre}
                    </option>
                  ))}
                </select>
                
                <button
                  onClick={() => {
                    setFilters({ familia_id: '' });
                    navigate('/catalogue');
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#e60026',
                    color: '#fff',
                    fontWeight: 600,
                    marginBottom: '8px'
                  }}
                >
                  {t.clear}
                </button>
              </div>
            </div>

            {/* Sort options */}
            <div className="catalogue-filters" style={{ marginBottom: 16 }}>
              <div className="catalogue-filters-header">
                <span className="catalogue-filters-title">{t.sort}</span>
              </div>
              <div className="catalogue-filters-content show">
                <select
                  id="sort-products"
                  value={sortOption}
                  onChange={e => setSortOption(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    marginBottom: '8px'
                  }}
                >
                  <option value="nombre-asc">{t.sortNameAsc}</option>
                  <option value="nombre-desc">{t.sortNameDesc}</option>
                  {!showingFamilias ? (
                    <>
                      <option value="precio-asc">{t.sortPriceAsc}</option>
                      <option value="precio-desc">{t.sortPriceDesc}</option>
                    </>
                  ) : (
                    <>
                      <option value="precio-asc">Productos (menor a mayor)</option>
                      <option value="precio-desc">Productos (mayor a menor)</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </CatalogueSidebar>
        )}

        {/* Product/Familia cards */}
        <div className="catalogue-grid-wrapper">
          <CatalogueGrid
            items={showingFamilias ? familiasWithThumbnails : products}
            renderItem={item => (
              showingFamilias
                ? <FamiliaCard 
                    key={item.id} 
                    familia={item} 
                    subfamiliaCount={item.subfamiliaCount}
                    validThumbnails={item.validThumbnails}
                    currentThumbnail={item.currentThumbnail}
                    onSelectFamilia={familiaId => {
                      setFilters(prev => ({ ...prev, familia_id: familiaId }));
                      setSelectedFamiliaId(familiaId);
                      navigate(`/catalogue?familia=${familiaId}`);
                    }}
                  />
                : <ProductCard
                    key={item.id}
                    product={item}
                    image={
                      item.thumbnail && item.thumbnail.trim() !== ""
                        ? item.thumbnail
                        : DEFAULT_THUMB_URL
                    }
                    lang={lang}
                    labels={{
                      codeLabel: t.id,
                      retailLabel: familiasList.find(f => f.id === item.familia_id)?.nombre || "",
                      wholesaleLabel: ""
                    }}
                    addToCartLabel={t.add}
                  />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default Catalogue;
